/**
 * @fileoverview Dammen Cell Component
 * @description Renders a single cell on the checkerboard
 */

import React, { memo } from "react";
import { TouchableOpacity, StyleSheet, View } from "react-native";
import { DammenPiece } from "./DammenPiece";
import { DammenCell as CellType, BoardPosition, Move, isDarkSquare } from "../types";

interface DammenCellProps {
  position: BoardPosition;
  cell: CellType;
  cellSize: number;
  isSelected: boolean;
  isValidMove: boolean;
  isValidCapture: boolean;
  isMustJump: boolean;
  onPress: (position: BoardPosition) => void;
  disabled?: boolean;
}

/**
 * Dammen Cell Component
 * Renders a single square on the board
 */
export const DammenCellComponent: React.FC<DammenCellProps> = memo(
  ({
    position,
    cell,
    cellSize,
    isSelected,
    isValidMove,
    isValidCapture,
    isMustJump,
    onPress,
    disabled = false,
  }) => {
    const isDark = isDarkSquare(position);

    const handlePress = () => {
      if (!disabled) {
        onPress(position);
      }
    };

    return (
      <TouchableOpacity
        style={[
          styles.cell,
          {
            width: cellSize,
            height: cellSize,
            backgroundColor: isDark ? "#8B4513" : "#DEB887",
          },
          isValidMove && styles.validMoveHighlight,
          isValidCapture && styles.validCaptureHighlight,
          isMustJump && styles.mustJumpHighlight,
        ]}
        onPress={handlePress}
        disabled={disabled || (!isDark && !cell)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`Cell ${position}${cell ? ` with ${cell.owner} ${cell.type}` : ""}`}
      >
        {/* Valid move indicator */}
        {isValidMove && !isValidCapture && (
          <View style={[styles.validMoveIndicator, { width: cellSize * 0.3, height: cellSize * 0.3 }]} />
        )}

        {/* Capture indicator */}
        {isValidCapture && (
          <View style={[styles.captureIndicator, { width: cellSize * 0.8, height: cellSize * 0.8 }]} />
        )}

        {/* Piece */}
        {cell && (
          <DammenPiece
            owner={cell.owner}
            type={cell.type}
            size={cellSize * 0.85}
            isSelected={isSelected}
          />
        )}
      </TouchableOpacity>
    );
  }
);

DammenCellComponent.displayName = "DammenCell";

const styles = StyleSheet.create({
  cell: {
    alignItems: "center",
    justifyContent: "center",
  },
  validMoveHighlight: {
    backgroundColor: "rgba(139, 69, 19, 0.7)",
  },
  validCaptureHighlight: {
    backgroundColor: "rgba(220, 38, 38, 0.3)",
  },
  mustJumpHighlight: {
    backgroundColor: "rgba(255, 215, 0, 0.3)",
  },
  validMoveIndicator: {
    position: "absolute",
    backgroundColor: "rgba(34, 197, 94, 0.6)",
    borderRadius: 100,
  },
  captureIndicator: {
    position: "absolute",
    borderWidth: 3,
    borderColor: "rgba(220, 38, 38, 0.8)",
    borderRadius: 100,
    borderStyle: "dashed",
  },
});

